/* eslint-disable react/jsx-no-useless-fragment */
import {
  Loading,
  LoadingBlock,
  Table,
  TableColumnHeader,
  TableColumnInstance,
} from '@orobix/orobix-fe-components'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroller'
import { useInfiniteQuery, useQueryClient } from 'react-query'

import { SortButton } from '../../components/SortButton/SortButton'
import { getOptimizationTypeStr, OptimizationType } from '../../types/dataset'
import {
  DONE,
  OptimizationProcess,
  OptimizationProcesses,
  RUNNING,
} from '../../types/optimizationProcesses'
import { Sorting, SortingDirection } from '../../types/sorting'
import { get } from '../../utils/api'
import { formatDateTime } from '../../utils/date'
import { getOptimizationProcessStatus } from '../../utils/optimizationProcesses'
import { getOffsetFromUrl, getQueryString } from '../../utils/queryString'
import { serializeSorting } from '../../utils/sorting'
import { createSocket } from '../../utils/webSocket'
import { useStyles } from './OptimizationProcessesSelectionTable.styles'

const TruncatedText = ({ text }: { text?: string }) => {
  if (!text) return <>-</>
  if (text.length <= 30) return <>{text}</>
  return <span title={text}>{text.slice(0, 30)}&hellip;</span>
}

type ColumnsKeys = keyof OptimizationProcess

type OptimizationProcessesSelectionTableProps = {
  onSelectProcess: (processId: number) => void
  filterByType?: OptimizationType
}

const useColumns = ({
  sorting,
  handleSort,
}: {
  sorting: Sorting
  handleSort: (key: ColumnsKeys, direction: SortingDirection) => void
}): TableColumnInstance<OptimizationProcess, ColumnsKeys>[] => {
  const { t } = useTranslation()

  return useMemo(
    () => [
      {
        id: 'id',
        header: (
          <TableColumnHeader
            title={t('Id')}
            sorting={
              <SortButton
                sort={sorting?.id}
                onSort={(direction) => handleSort('id', direction)}
              />
            }
          />
        ),
        th: true,
        template: ({ id }) => `#${id}`,
      },
      {
        id: 'name',
        header: <TableColumnHeader title={t('Name')} />,
        th: true,
        template: ({ name }) => <TruncatedText text={name} />,
      },
      {
        id: 'notes',
        header: <TableColumnHeader title={t('Notes')} />,
        th: true,
        template: ({ notes }) => <TruncatedText text={notes} />,
      },
      {
        id: 'optimizationProcessType',
        header: (
          <TableColumnHeader
            title={t('Type')}
            sorting={
              <SortButton
                sort={sorting?.optimizationProcessType}
                onSort={(direction) =>
                  handleSort('optimizationProcessType', direction)
                }
              />
            }
          />
        ),
        th: true,
        template: ({ optimizationProcessType }) =>
          optimizationProcessType
            ? getOptimizationTypeStr(
                optimizationProcessType as OptimizationType
              )
            : '-',
      },
      {
        id: 'sync',
        header: (
          <TableColumnHeader
            title={t('Sync')}
            sorting={
              <SortButton
                sort={sorting?.sync}
                onSort={(direction) => handleSort('sync', direction)}
              />
            }
          />
        ),
        th: true,
        template: ({ sync }) => (sync ? formatDateTime(sync) : '-'),
      },
      {
        id: 'referenceOptimizationProcess',
        header: (
          <TableColumnHeader
            title={t('Source Opt. proc')}
            sorting={
              <SortButton
                sort={sorting?.referenceOptimizationProcess}
                onSort={(direction) =>
                  handleSort('referenceOptimizationProcess', direction)
                }
              />
            }
          />
        ),
        th: true,
        template: ({ referenceOptimizationProcess }) =>
          referenceOptimizationProcess
            ? `#${referenceOptimizationProcess}`
            : '-',
      },
      {
        id: 'version',
        header: (
          <TableColumnHeader
            title={t('Version')}
            sorting={
              <SortButton
                sort={sorting?.version}
                onSort={(direction) => handleSort('version', direction)}
              />
            }
          />
        ),
        th: true,
        template: ({ version }) => version || '-',
      },
      {
        id: 'status',
        header: (
          <TableColumnHeader
            title={t('Status')}
            sorting={
              <SortButton
                sort={sorting?.status}
                onSort={(direction) => handleSort('status', direction)}
              />
            }
          />
        ),
        template: ({ status }) => (
          <>
            {getOptimizationProcessStatus(status)}{' '}
            {status === RUNNING && <Loading />}
          </>
        ),
      },
      {
        id: 'user',
        header: (
          <TableColumnHeader
            title={t('User')}
            sorting={
              <SortButton
                sort={sorting?.user}
                onSort={(direction) => handleSort('user', direction)}
              />
            }
          />
        ),
        th: true,
        template: ({ user }) => user || '-',
      },
      {
        id: 'createdAt',
        header: (
          <TableColumnHeader
            title={t('Created on')}
            sorting={
              <SortButton
                sort={sorting?.createdAt}
                onSort={(direction) => handleSort('createdAt', direction)}
              />
            }
          />
        ),
        template: ({ createdAt }) => formatDateTime(createdAt),
      },
    ],
    [sorting, t, handleSort]
  )
}

export const OptimizationProcessesSelectionTable = ({
  onSelectProcess,
  filterByType,
}: OptimizationProcessesSelectionTableProps) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const queryClient = useQueryClient()
  const [sorting, setSorting] = useState<Sorting>({})
  const limit = 20

  const {
    data: response,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    ['optimization-processes-selection', sorting, filterByType],
    ({ pageParam }) =>
      get<OptimizationProcesses>(
        `/optimization-processes/?${getQueryString({
          offset: pageParam,
          limit,
          ordering: serializeSorting(sorting),
          ...(filterByType && { optimization_type: filterByType }),
          status: DONE,
        })}`
      ),
    {
      getNextPageParam: (lastPage) => getOffsetFromUrl(lastPage.next),
      retry: false,
      keepPreviousData: true,
    }
  )

  const data = response?.pages.map((p) => p.results).flat() || []
  const isEmpty = !isLoading && data.length === 0

  const handleSort = useCallback(
    (key: ColumnsKeys, direction: SortingDirection) => {
      setSorting({ [key]: direction })
    },
    []
  )

  const columns = useColumns({ sorting, handleSort })

  useEffect(() => {
    const websocket = createSocket('/optimization-process/status')

    websocket.onmessage = (event: MessageEvent<string>) => {
      queryClient.invalidateQueries(['optimization-processes-selection'])
    }

    return () => {
      websocket.close()
    }
  }, [queryClient])

  return (
    <div className={classes.scrollContainer}>
      <InfiniteScroll
        loadMore={() => fetchNextPage()}
        hasMore={hasNextPage}
        loader={<LoadingBlock message={t('Loading')} key="loading" />}
        useWindow={false}
      >
        <Table
          columns={columns}
          data={data.map((row) => ({
            ...row,
            className: 'selectable',
            title: 'Click to select current process',
          }))}
          alternateRows
          stickyHeader
          isEmpty={isEmpty}
          emptyMessage={t(
            `There's nothing here. Try creating a new {{ itemName }}.`,
            { itemName: t('optimization process') }
          )}
          onRowClick={(row) => {
            onSelectProcess(row.id)
          }}
        />
      </InfiniteScroll>
    </div>
  )
}
